import React, { useState, useEffect, useCallback, useRef } from "react";

/**
 * ProductImageGallery
 * A modern, accessible, high-performance product image gallery & lightbox
 * Supporting main hero preview, integrated bottom scroll strip, and fullscreen lightbox.
 */
const ProductImageGallery = ({
  images = [],
  productName = "Product",
  className = "",
  readOnly = true,
  emptySubtitle = "",
  aspectRatio = "aspect-square", // aspect-square or aspect-[4/3]
}) => {
  const imageList = Array.isArray(images) ? images.filter((img) => typeof img === "string" && img.trim()) : [];
  const [activeIndex, setActiveIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [hasError, setHasError] = useState({});
  const scrollContainerRef = useRef(null);
  const thumbnailRefs = useRef([]);

  // Reset active index if list changes
  useEffect(() => {
    if (activeIndex >= imageList.length) {
      setActiveIndex(0);
    }
  }, [imageList.length, activeIndex]);

  // Handle keyboard navigation for lightbox
  const handleKeyDown = useCallback(
    (e) => {
      if (!isLightboxOpen) return;
      if (e.key === "Escape") {
        setIsLightboxOpen(false);
        setZoomLevel(1);
      } else if (e.key === "ArrowRight") {
        setActiveIndex((prev) => (prev + 1) % imageList.length);
        setZoomLevel(1);
      } else if (e.key === "ArrowLeft") {
        setActiveIndex((prev) => (prev - 1 + imageList.length) % imageList.length);
        setZoomLevel(1);
      }
    },
    [isLightboxOpen, imageList.length]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // Smooth mouse-wheel scrolling for horizontal thumbnail strip
  const handleThumbnailWheel = (e) => {
    if (scrollContainerRef.current && e.deltaY !== 0) {
      scrollContainerRef.current.scrollLeft += e.deltaY;
    }
  };

  // Scroll active thumbnail into view smoothly
  useEffect(() => {
    if (thumbnailRefs.current[activeIndex]) {
      thumbnailRefs.current[activeIndex].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [activeIndex]);

  const handleImageError = (index) => {
    setHasError((prev) => ({ ...prev, [index]: true }));
  };

  const currentImage = imageList[activeIndex];

  // Empty State
  if (imageList.length === 0) {
    return (
      <div
        className={`bg-white rounded-[16px] border border-primary-100 p-6 flex flex-col items-center justify-center text-center ${aspectRatio} ${className}`}
      >
        <div className="w-16 h-16 rounded-2xl bg-primary-50/70 border border-primary-100 flex items-center justify-center mb-3 shadow-2xs">
          <svg
            className="w-8 h-8 text-primary-400"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
            <circle cx="8" cy="8" r="2"></circle>
            <path d="m21 15-5-5L5 21"></path>
          </svg>
        </div>
        <h4 className="text-[13px] font-bold text-secondry-800 mb-1">No Product Images</h4>
        <p className="text-[11px] text-primary-400 max-w-[200px] leading-relaxed">
          {emptySubtitle || (readOnly
            ? "No visual imagery uploaded for this product yet."
            : "Upload package, label, or strip photos for enhanced identification.")}
        </p>
      </div>
    );
  }

  return (
    <div
      className={`bg-white rounded-[16px] border border-primary-200/90 shadow-2xs overflow-hidden flex flex-col ${className}`}
    >
      {/* Main Hero Image Area */}
      <div className="relative group/hero w-full bg-gray-50/50 flex items-center justify-center p-3 select-none">
        <div
          className={`${aspectRatio} w-full flex items-center justify-center cursor-zoom-in relative`}
          onClick={() => {
            setIsLightboxOpen(true);
            setZoomLevel(1);
          }}
        >
          {hasError[activeIndex] ? (
            <div className="flex flex-col items-center justify-center text-primary-400 p-4">
              <svg className="w-10 h-10 mb-2 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M6 18L18 6M6 6l12 12" />
              </svg>
              <span className="text-[11px] font-medium">Failed to load image</span>
            </div>
          ) : (
            <img
              src={currentImage}
              alt={`${productName} view ${activeIndex + 1}`}
              fetchpriority="high"
              onError={() => handleImageError(activeIndex)}
              className="w-full h-full object-contain transition-transform duration-300 group-hover/hero:scale-105"
            />
          )}

          {/* Top Left: Badges Overlay */}
          <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5 z-10 pointer-events-none">
            {activeIndex === 0 && (
              <span className="px-2 py-0.5 bg-emerald-600 text-white text-[9px] sm:text-[10px] font-bold rounded-md uppercase tracking-wider shadow-2xs">
                Cover
              </span>
            )}
            {imageList.length > 1 && (
              <span className="px-1.5 py-0.5 bg-secondry-800/80 text-white text-[9px] sm:text-[10px] font-medium rounded-md backdrop-blur-xs shadow-2xs font-mono">
                {activeIndex + 1}/{imageList.length}
              </span>
            )}
          </div>

          {/* Top Right: Enlarge Icon Button */}
          <button
            type="button"
            className="absolute top-2.5 right-2.5 z-20 w-8 h-8 rounded-lg bg-white/95 hover:bg-white text-secondry-700 hover:text-primary-600 border border-primary-200/90 shadow-2xs backdrop-blur-xs transition-all duration-150 cursor-pointer flex items-center justify-center hover:scale-105 active:scale-95 group/enlarge"
            onClick={(e) => {
              e.stopPropagation();
              setIsLightboxOpen(true);
              setZoomLevel(1);
            }}
            title="Enlarge Image"
            aria-label="Enlarge Image"
          >
            <svg
              className="w-4 h-4 text-secondry-700 group-hover/enlarge:text-primary-600 transition-colors"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 3 21 3 21 9"></polyline>
              <polyline points="9 21 3 21 3 15"></polyline>
              <line x1="21" y1="3" x2="14" y2="10"></line>
              <line x1="3" y1="21" x2="10" y2="14"></line>
            </svg>
          </button>

          {/* Left & Right Hero Navigation Arrows (if > 1 image) */}
          {imageList.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveIndex((prev) => (prev - 1 + imageList.length) % imageList.length);
                }}
                className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-7 h-7 rounded-full bg-white/90 hover:bg-white text-secondry-700 hover:text-primary-600 border border-primary-200/80 shadow-xs flex items-center justify-center opacity-0 group-hover/hero:opacity-100 transition-all cursor-pointer hover:scale-105 active:scale-95"
                title="Previous Image"
                aria-label="Previous Image"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveIndex((prev) => (prev + 1) % imageList.length);
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-7 h-7 rounded-full bg-white/90 hover:bg-white text-secondry-700 hover:text-primary-600 border border-primary-200/80 shadow-xs flex items-center justify-center opacity-0 group-hover/hero:opacity-100 transition-all cursor-pointer hover:scale-105 active:scale-95"
                title="Next Image"
                aria-label="Next Image"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Bottom Horizontal Scroll Gallery Strip (inside the image block) */}
      {imageList.length > 1 && (
        <div className="border-t border-primary-100 bg-white px-2.5 py-2">
          <div
            ref={scrollContainerRef}
            onWheel={handleThumbnailWheel}
            className="flex items-center gap-2 overflow-x-auto scrollbar-hide py-1 px-1 scroll-smooth"
          >
            {imageList.map((img, idx) => {
              const isSelected = activeIndex === idx;
              return (
                <button
                  key={idx}
                  ref={(el) => (thumbnailRefs.current[idx] = el)}
                  type="button"
                  onClick={() => setActiveIndex(idx)}
                  className={`relative shrink-0 w-12 h-12 rounded-[10px] p-0.5 bg-white transition-all duration-150 cursor-pointer flex items-center justify-center ${
                    isSelected
                      ? "border-2 border-primary-600 ring-2 ring-primary-200 shadow-2xs"
                      : "border border-primary-100 hover:border-primary-400 opacity-60 hover:opacity-100"
                  }`}
                  aria-label={`View image ${idx + 1}`}
                >
                  <img
                    src={img}
                    alt={`Thumbnail ${idx + 1}`}
                    loading="lazy"
                    fetchpriority="low"
                    className="w-full h-full object-contain rounded-[7px]"
                  />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Fullscreen Lightbox Modal */}
      {isLightboxOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm p-4 sm:p-6 animate-fadeIn"
          onClick={() => {
            setIsLightboxOpen(false);
            setZoomLevel(1);
          }}
        >
          {/* Top Controls */}
          <div
            className="absolute top-4 inset-x-4 flex items-center justify-between text-white z-20"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3">
              <span className="text-[14px] font-bold text-white tracking-wide truncate max-w-[280px] sm:max-w-md">
                {productName}
              </span>
              <span className="px-2 py-0.5 bg-white/20 text-white text-[11px] font-mono rounded-md">
                {activeIndex + 1} / {imageList.length}
              </span>
            </div>

            <div className="flex items-center gap-2">
              {/* Zoom In */}
              <button
                type="button"
                onClick={() => setZoomLevel((z) => Math.min(z + 0.3, 3))}
                className="p-2 rounded-lg bg-white/10 hover:bg-white/25 text-white transition-all cursor-pointer"
                title="Zoom In"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
              </button>
              {/* Zoom Out */}
              <button
                type="button"
                onClick={() => setZoomLevel((z) => Math.max(z - 0.3, 0.7))}
                className="p-2 rounded-lg bg-white/10 hover:bg-white/25 text-white transition-all cursor-pointer"
                title="Zoom Out"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4" />
                </svg>
              </button>
              {/* Reset Zoom */}
              {zoomLevel !== 1 && (
                <button
                  type="button"
                  onClick={() => setZoomLevel(1)}
                  className="px-2 py-1 text-[11px] rounded-lg bg-white/10 hover:bg-white/25 text-white transition-all font-semibold"
                >
                  Reset
                </button>
              )}
              {/* Close */}
              <button
                type="button"
                onClick={() => {
                  setIsLightboxOpen(false);
                  setZoomLevel(1);
                }}
                className="p-2 rounded-lg bg-white/10 hover:bg-rose-500/80 text-white transition-all ml-2 cursor-pointer"
                title="Close (Esc)"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Main Image in Lightbox */}
          <div
            className="max-w-4xl max-h-[75vh] w-full flex items-center justify-center p-2 relative overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={currentImage}
              alt={`${productName} full view`}
              style={{ transform: `scale(${zoomLevel})` }}
              className="max-h-[72vh] max-w-full object-contain rounded-lg transition-transform duration-200 select-none shadow-2xl"
            />
          </div>

          {/* Previous Button */}
          {imageList.length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setActiveIndex((prev) => (prev - 1 + imageList.length) % imageList.length);
                setZoomLevel(1);
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/15 hover:bg-white/30 text-white transition-all cursor-pointer backdrop-blur-xs"
              title="Previous (Left arrow)"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          {/* Next Button */}
          {imageList.length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setActiveIndex((prev) => (prev + 1) % imageList.length);
                setZoomLevel(1);
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/15 hover:bg-white/30 text-white transition-all cursor-pointer backdrop-blur-xs"
              title="Next (Right arrow)"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}

          {/* Bottom Thumbnails Strip */}
          {imageList.length > 1 && (
            <div
              className="absolute bottom-4 inset-x-4 flex items-center justify-center gap-2 z-20 overflow-x-auto py-1"
              onClick={(e) => e.stopPropagation()}
            >
              {imageList.map((img, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => {
                    setActiveIndex(idx);
                    setZoomLevel(1);
                  }}
                  className={`w-12 h-12 rounded-[10px] overflow-hidden border-2 transition-all duration-150 bg-black/60 p-0.5 cursor-pointer flex items-center justify-center ${
                    activeIndex === idx
                      ? "border-2 border-white ring-2 ring-white/50 shadow-md opacity-100"
                      : "border border-white/20 opacity-50 hover:opacity-100"
                  }`}
                >
                  <img src={img} alt={`Thumb ${idx + 1}`} className="w-full h-full object-contain rounded-[6px]" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductImageGallery;
